import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { pinSvgIconPink } from "assets/icons";

import { CloseButton, FormButton, ServiceSvgIcon } from "reusableComponents";
import { deleteBooking, transformBookingData } from "services/booking.services";
import { useNavigate } from "react-router";

import styles from "./styles.module.scss";
import { BookingForm, ReusableConfirmationModal } from "modals";
import { useLanguage, useModal } from "context";
import { fetchSpaceData } from "services/spacePage.services";
import { useSelector } from "react-redux";
import { selectIsAuthenticated } from "store/auth/selectors";
import { getDispositionTypeByKey } from "helpers/getSpaceDispositionTypes";
import { getStreetName } from "views/SpacePage/helpers";

const BasketCard = ({ basketItem, handleEdit, handleDeleteBooking }) => {
  const { t } = useTranslation("global");
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { language } = useLanguage;
  const [showDetails, setShowDetails] = useState(false);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const accessToken = useSelector((state) => state.auth.token);

  const {
    place,
    total_with_vat,
    total,
    vat,
    provider_name,
    capacity,
    coworking_stations,
    disposition_type,
    services,
    id,
    training_id,
    trainee_bookable,
  } = basketItem;

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const { booking_hours, day_services } = transformBookingData(basketItem);

  const currentUser = useSelector((state) => state.auth.currentUser);

  return (
    <div className={styles.basketCard}>
      <div className={styles.header}>
        <p className={styles.title}>{place.name}</p>
        <div className={styles.headerButtons}>
          <button
            onClick={() => handleEdit(id, place)}
            className={styles.editButton}
          >
            {t("dashboard.spacesManagement.edit")}
          </button>
          <button
            onClick={() =>
              openModal({
                content: (
                  <ReusableConfirmationModal
                    message={t("basket.removalConfirmationText")}
                    onConfirm={() => handleDeleteBooking(id)}
                  />
                ),
                title: t("basket.removalConfirmationTitle"),
              })
            }
            className={styles.deleteButton}
          >
            {t("dashboard.spacesManagement.delete")}
          </button>
          {showDetails && (
            <div className={styles.closeButton}>
              <CloseButton
                closeCallback={toggleDetails}
                className={styles.closeButton}
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.images}>
          {place.images.slice(0, 1).map((image) => (
            <img
              key={image.id}
              src={image.thumb_image_url}
              alt={image.description}
              className={styles.image}
            />
          ))}
        </div>
        <div className={styles.details}>
          <div>
            <p className={styles.item}>
              <span
                className={styles.icon}
                style={{ backgroundImage: `url(${pinSvgIconPink})` }}
              ></span>
              <strong className={styles.value}>{`${
                training_id
                  ? place.address.address
                  : getStreetName(place.address.address)
              }, ${place.address.city}`}</strong>
            </p>
            <span className={`${styles.item} ${styles.spaceId}`}>
              <span className={styles.label}>{t("space.space")}</span>{" "}
              <span className={styles.value}>{place.id}</span>
            </span>

            <p className={styles.item}>
              <span className={styles.label}>{t("basket.provider_name")}</span>
              <span className={styles.value}> {provider_name}</span>
            </p>
          </div>
        </div>
        {!currentUser?.trainee && !currentUser?.master_seeker && (
          <div
            className={`${styles.priceDetails} ${
              !showDetails ? styles.hideOnMobile : ""
            }`}
          >
            <div className={styles.item}>
              <span className={styles.label}>{t("basket.totalDutyFree")}:</span>
              <span className={styles.value}>{total} Euro (€)</span>
            </div>
            <div className={styles.item}>
              <span className={styles.label}>{t("basket.VAT")}: </span>
              <span className={styles.value}>{vat} Euro (€)</span>
            </div>
            {total_with_vat && (
              <div className={styles.item}>
                <span className={styles.label}>
                  {t("basket.totalTaxesIncluded")}:
                </span>
                <span className={styles.value}>
                  {total_with_vat} Euro (€)
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      {showDetails && (
        <div className={styles.extraDetails}>
          <div className={styles.item}>
            <span className={styles.label}>
              {t("booking.step1.dates_and_hours")}:{" "}
            </span>
            <span className={styles.value}>
              {Array.isArray(booking_hours) &&
                booking_hours.length > 0 &&
                booking_hours.map((slot, idx) => (
                  <p key={`${booking_hours} ${slot.from} ${idx}`}>
                    {slot.date} {t("booking.step1.from")} {slot.from}{" "}
                    {t("booking.step1.to")} {slot.to}
                  </p>
                ))}
            </span>
          </div>
          {place?.placeUsage?.key !== "coworking" ? (
            <div className={styles.item}>
              <span className={styles.label}>
                {t("booking.step1.numberOfPeople")}:
              </span>{" "}
              <span className={styles.value}> {capacity}</span>
            </div>
          ) : (
            <div className={styles.item}>
              <span className={styles.label}>
                {t("booking.step1.coworkingStations")}:
              </span>{" "}
              <span className={styles.value}>{coworking_stations}</span>
            </div>
          )}
          {disposition_type ? (
            <div className={styles.item}>
              <span className={styles.label}>
                {t("booking.step1.disposition_type")}{" "}
              </span>
              <span className={styles.value}>
                {getDispositionTypeByKey(disposition_type, t).name}
              </span>
            </div>
          ) : (
            <div></div>
          )}
          <div className={styles.item}>
            <span className={styles.label}>
              {t("dashboard.bookings.reserved-options")}:
            </span>
            {day_services &&
              day_services.length > 0 &&
              services.map((service) => (
                <div className={styles.value} key={service.name}>
                  <span>{service.name}</span>{" "}
                  <ServiceSvgIcon
                    size="small"
                    fillColor="#90abb1"
                    color="white"
                    iconPath={service.image_url}
                  />
                  x{service.quantity}
                </div>
              ))}
          </div>
        </div>
      )}
      <div className={styles.footer}>
        {!showDetails ? (
          <div className={styles.footerButton}>
            <FormButton
              size="medium"
              onClick={toggleDetails}
              variant="gray"
              label={t("basket.show-more-info")}
            />
          </div>
        ) : (
          <>
            <div className={styles.footerButton}>
              <FormButton
                onClick={() => navigate("/static/cgu")}
                variant="gray"
                label={t("basket.viewCgv")}
                size="medium"
              />
            </div>
            <div className={styles.footerButton}>
              <FormButton
                onClick={() => navigate(`/spaces/${place.id}`)}
                variant="secondary"
                size="medium"
                label={t("basket.addAnotherBooking")}
              />
            </div>
          </>
        )}
        <div className={styles.footerButton}>
          <FormButton
            onClick={() => handleEdit(id, place)}
            variant="primary"
            size="medium"
            label={t("basket.continueBooking")}
          />
        </div>
      </div>
    </div>
  );
};

export default BasketCard;